import { Icon } from "@iconify/react";
import { Card, Select } from "antd";
import React from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const { Option } = Select;

interface ChartCardProps {
  data: any[];
  title?: string;
  userTypes?: string[]; // List of keys to show in tooltip
  onYearChange?: (year: string) => void;
  selectedYear?: string;
}

const COLORS = ["#712B90", "#310F7F", "#00C247", "#FF3333", "#FF9900", "#1E90FF"];

export const AreaChartCard: React.FC<ChartCardProps> = ({
  data,
  title,
  userTypes,
  onYearChange,
  selectedYear = "2025", // fallback if not provided
}) => {
  const defaultUserTypes = ["Total"];
  const typesToShow = userTypes || defaultUserTypes;

  const getDataKey = (label: string) => label.toLowerCase().replace(/\s+/g, "");

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0]?.payload;

      return (
        <div className="bg-white p-3 rounded-md shadow-md border border-gray-200">
          <p className="font-semibold mb-1">{label}</p>
          {typesToShow.map((type, idx) => {
            const key = getDataKey(type);
            return (
              <p
                key={type}
                style={{ color: COLORS[idx % COLORS.length] }}
              >
                {type}: {item?.[key] ?? "N/A"}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      title={<span className="text-2xl font-bold">{title || "Users"}</span>}
      extra={
        <div className="flex items-center bg-white border border-gray-300 rounded-full shadow-sm px-3 py-1 hover:border-[#712B90] transition-colors duration-300">
          <Icon
            icon="bi:calendar"
            className="text-gray-600 mr-2"
          />
          <Select
            value={selectedYear}
            style={{ width: 100 }}
            bordered={false}
            dropdownMatchSelectWidth={false}
            onChange={(value) => onYearChange?.(value)}
            suffixIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            }
            className="ant-select-compact"
          >
            <Option value="2025">2025</Option>
            <Option value="2026">2026</Option>
          </Select>
        </div>
      }
      className="w-full max-w-4xl !rounded-2xl shadow-lg"
    >
      <div style={{ marginLeft: "-30px" }}>
        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <AreaChart data={data}>
            <XAxis dataKey="name" />
            <YAxis />
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey={getDataKey(typesToShow[0])}
              stroke={COLORS[0]}
              fill="none"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
